import {ReactNode} from 'react';

import {ApiSortings} from './api';
import {AddressExtend} from './geo';

export interface ProfileCountries {
    code: string;
    country: string;
    countryFr?: string;
}

export interface ProfileCommonInfo {
    fullName: string;
    shortName: string;
    participantId: string;
    email: string;
    legalAddress: string;
    ceo: string;
    type: ProfileRole;
    legalIdentity: string;
    legalName: string;
}

export enum ProfileRole {
    MANUFACTURER = 'MANUFACTURER',
    IMPORTER = 'IMPORTER',
    DISTRIBUTOR = 'DISTRIBUTOR',
    RETAILER = 'RETAILER',
}

export enum ProfileType {
    DIRECTOR = 'DIRECTOR',
    EDS_DIRECTOR = 'EDS_DIRECTOR',
    EMPLOYEE = 'EMPLOYEE',
}

// /api/v1/service/profile/factories/{factoryId}
export interface Factory {
    id: number;
    address?: string;
    addressId?: number;
    manufacturerId: number;
    name: string;
    code: string;
    country: string;
    countryCode: string;
    factoryLines: {
        id: number;
        factoryId: number;
        code: string;
        name: string;
        productName: string;
        lastActivityTime: Date;
        productsCount: number;
        packsCount: number;
        defectPercentage: number;
        active: boolean;
    }[];
}

// /api/v1/service/profile/factories/{factoryId}/factoryDetails-lines
export interface FactoryLineStatus {
    id: number;
    factoryId: number;
    active: boolean;
    name: string;
    code: string;
    productName: string;
}

export interface FactoryLineStatisticsRecord {
    id: string;
    data: {
        x: string;
        y: number;
        date: Date;
    }[];
}

export interface FactoryLineActivityRecord {
    date: string;
    isActive: boolean;
}

// /api/v1/service/profile/factories/{factoryId}/factoryDetails-lines/{factoryLineId}
export interface FactoryLine {
    id: number;
    factoryId: number;
    code: string;
    name: string;
    productName: string;
    lastActivityTime: Date;
    markingCodesCount: number;
    goodsCount: number;
    defectPercentage: number;
    active: boolean;
    activity: {
        active: true;
        timestamp: Date;
    }[];
}

export interface StaffListRequest {
    paging: {
        pageNumber: number;
        pageSize: number;
    };
    sortings: ApiSortings;
    filters: StaffTableFilter[];
    search?: string;
}

export interface StaffTableFilter {
    id: string;
    value: string;
}

export enum StaffAccessors {
    id = 'id',
    name = 'fullName',
    email = 'email',
    phone = 'phone',
    storageLocation = 'storageLocation',
    actions = 'actions',
}

export interface StaffListRawRow {
    id: number;
    clientId: number;
    username: string;
    email: string;
    emailVerified: boolean;
    phone: string;
    phoneVerified: boolean;
    firstName: string;
    middleName: string;
    lastName: string;
    storageLocation?: string;
}

export interface StaffListFormattedRow {
    id: string;
    [StaffAccessors.name]: ReactNode;
    [StaffAccessors.email]: string;
    [StaffAccessors.phone]: string;
    [StaffAccessors.storageLocation]?: string;
    [StaffAccessors.actions]?: ReactNode;
}

export interface StaffPaginationContent<C> {
    content: C[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
}

export enum StorageAccessors {
    storageName = 'storageName',
    address = 'address',
    storageType = 'storageType',
}

export interface StorageListRequest {
    paging: {
        pageNumber: number;
        pageSize: number;
    };
    filters: StaffTableFilter[];
    sortings: ApiSortings;
}

export interface StorageRawRow {
    id?: number;
    clientId: number;
    storageName: string;
    address: string;
    storageType?: string;
    storageGln?: string;
    storageTypeId: number;
}

export interface StorageFormattedRow {
    [StorageAccessors.storageName]: ReactNode;
    [StorageAccessors.address]: string;
    [StorageAccessors.storageType]: string;
}

export interface StoragePaginationContent<C> {
    data: C[];
    totalElements: number;
    totalPages: number;
}

export enum FactoriesAccessors {
    id = 'id',
    name = 'name',
    address = 'address',
    manufacturerId = 'manufacturerId',
    linesCount = 'linesCount',
    code = 'code',
}

export interface FactoriesListRawRow {
    id: string;
    name: string;
    address: string;
    manufacturerId: string;
    factoryLinesTotalCount: number;
    countryCode: string;
    code: string;
}

export interface FactoriesListFormattedRow {
    id: string;
    name: ReactNode;
    address: string;
    manufacturerId: string;
    linesCount: number;
    code: string;
}

export interface CreateFactoryLine {
    id?: number | string;
    customId?: number | string;
    storedId?: number | string;
    name: string;
    productName?: string;
}

export interface CreateFactory {
    id?: number;
    customId?: string;
    name: string;
    address?: string;
    addressId?: number;
    country?: string;
    countryCode: string;
    factoryLines: CreateFactoryLine[];
}

export interface EditFactoryRaw {
    name: string;
    address: string;
    addressId?: number;
    factoryLines: {
        id?: number;
        name: string;
    }[];
}

export interface EditFactoryLineRaw {
    id?: number;
    name: string;
    productName: string;
}

export interface EditFactoryRequestData {
    factoryUpdate?: {
        name: string;
        address: string;
        addressId?: number;
        country: string | null;
        countryCode: string | null;
    };
    factoryLineOperations: CreateFactoryLine[];
}

export enum OrganizationAccessors {
    taxIdNumber = 'taxIdNumber',
    legalName = 'legalName',
    address = 'address',
    email = 'email',
    phone = 'phone',
    balance = 'balance',
    actions = 'actions',
}

export interface OrganizationRawRow {
    id: number;
    taxIdNumber: string;
    ein?: string;
    legalName: string;
    shortName?: string;
    country: string;
    postalCode?: string;
    address: AddressExtend;
    phone: string;
    email: string;
    ceo: string;
    gln?: string;
    vatId?: string;
    userId: number;
    isValidated: boolean;
    balance: number;
}

export interface OrganizationFormattedRow {
    taxIdNumber: string;
    legalName: string;
    address: string;
    phone: string;
    email: string;
    balance: ReactNode;
    actions: ReactNode;
}

export interface OrganizationListRequest {
    paging: {
        pageNumber: number;
        pageSize: number;
    };
    filters: StaffTableFilter[];
    sortings: ApiSortings;
    taxIdNumber?: string;
}

export interface OrganizationPaginationContent {
    data: OrganizationRawRow[];
    totalElements: number;
    totalPages: number;
}

export interface Customs {
    id?: number;
    number: string;
    name: string;
    address: string;
}

export interface CustomsRow {
    id?: number;
    number: string;
    name: ReactNode | string;
    address: string;
    actions?: ReactNode;
}

export interface CustomsUserRow {
    userId: number;
    clientId: number;
    userName: string;
    email: string;
    emailVerified: boolean;
    phone: string;
    phoneVerified: boolean;
    firstName: string;
    middleName: string;
    lastName: string;
    customs: CustomsRow[];
    actions?: ReactNode;
}

export interface CreateCustomsUser {
    registrationRequest: {
        id?: string;
        email: string;
        password?: string;
        phone?: string;
        type: 'EMPLOYEE';
        first_name: string;
        middle_name?: string;
        last_name: string;
        contact_email?: string;
        personal_tin?: string;
    };
    customsIdList: number[];
}

export interface UpdateCustomsUser {
    firstName: {value: string; reset: boolean};
    middleName: {value: string; reset: boolean};
    lastName: {value: string; reset: boolean};
    phone: {value: string; reset: boolean};
    customsIdList: number[];
    id?: string;
}

export interface CustomsPaginationContent<Data = CustomsRow> {
    data: Data[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
}
